















































































import Component, { mixins } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { Call, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import AudioPlayer from "@/lib/audioPlayer";
import { Debounce } from "@/lib/decorators";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import MobileSwitchCam from "@/components/MobileSwitchCam";
import ModalView from "@/components/ModalView";
import CallStateView from "./CallStateView.vue";
import CallStatusView from "./CallStatusView";

const CLICK_DEBOUNCE_DELAY = 300;

@Component({
  name: "CallModalView",
  components: {
    CallStatusView,
    CallStateView,
    ModalView,
    BaseIcon,
    BaseAvatar,
    BaseButton,
    BaseConfirm,
    MobileSwitchCam,
  },
})
export default class CallModalView extends mixins(WithAuthUser) {
  @Prop({ type: Boolean, default: false })
  public readonly showConnectionError!: boolean;

  @Prop({ type: String, default: "" })
  public readonly connectionErrorMessage!: string;

  @Prop({ required: true })
  public readonly opponentUser!: UserPublicData;

  @Prop({ required: true })
  public readonly call!: Call | null;

  @Prop({ required: true })
  public readonly opponentStream!: MediaStream;

  @Prop({ required: true })
  public readonly localStream!: MediaStream;

  @Prop({ required: true })
  public localReadyVideoState!: boolean;

  @Prop({ required: true })
  public opponentReadyVideoState!: boolean;

  @Prop({ required: true })
  public readonly localVideoState!: boolean;

  @Prop({ required: true })
  public opponentVideoState!: boolean;

  @Prop({ default: false })
  public loading!: boolean;

  public get isVideoStream(): boolean {
    return this.opponentVideoState && this.localVideoState;
  }

  public get processingVideoRequest(): boolean {
    return this.localReadyVideoState && !this.opponentReadyVideoState;
  }

  public get showConfirmVideoRequest(): boolean {
    return !this.localReadyVideoState && this.opponentReadyVideoState;
  }

  public get processingCall() {
    return this.call?.status === CallStatus.NEW;
  }

  public get titleVideoRequest() {
    return $t("{0} asks for video chat", [this.opponentUser.screenName]);
  }

  public get iconVideo() {
    return this.isVideoStream ? "sci-video-off" : "sci-video";
  }

  public get showAvatar() {
    return !this.isVideoStream && !this.showConnectionError && !this.processingVideoRequest;
  }

  public get showVideoButton() {
    return !this.processingVideoRequest && !this.processingCall && !this.callEnded && !this.showConnectionError;
  }

  public get showHangupButton() {
    return !this.callEnded || this.showConnectionError;
  }

  public get callEnded() {
    return (
      (this.call && [CallStatus.CANCELLED, CallStatus.DECLINED, CallStatus.ENDED].includes(this.call.status)) || false
    );
  }

  public get localVideoTracks(): MediaStreamTrack[] {
    return this.localStream.getVideoTracks();
  }

  public get mirrorVideo() {
    const settings = this.localVideoTracks.length > 0 && this.localVideoTracks[0].getSettings();
    return !settings || !settings.facingMode || settings.facingMode === "user";
  }

  public get showSwitchCam() {
    return this.localVideoTracks.length > 0 && !this.processingVideoRequest;
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onVideoToggle() {
    if (this.loading || this.opponentReadyVideoState !== this.localReadyVideoState) {
      return;
    }
    this.$emit("toggleVideo");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onAgreeClick() {
    this.$emit("videoRequestAccept");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onChangeDeviceClick(constraints: MediaTrackConstraints) {
    this.$emit("changeDevice", constraints);
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onCallStateViewClick() {
    this.showConnectionError ? this.$emit("callBack") : this.$emit("cancelVideoRequest");
  }

  @Debounce(CLICK_DEBOUNCE_DELAY)
  public onVideoRequestDeclineClick() {
    this.$emit("videoRequestDecline");
  }

  @Watch("processingVideoRequest")
  public resetErrorMessage(processingVideoRequest: boolean) {
    if (processingVideoRequest) {
      this.$emit("resetErrorMessage");
    }
  }

  @Watch("opponentStream")
  public async onOpponentStreamChange(opponentStream: MediaStream) {
    const player = getElement(this.$refs.player) as HTMLVideoElement;
    player.pause();
    const hasMedia = opponentStream.getTracks().length > 0;
    if (hasMedia) {
      player.srcObject = opponentStream;
      await player.play();
    }
  }

  @Watch("localStream")
  public async onLocalStreamChange(localStream: MediaStream) {
    const player = getElement(this.$refs.localPlayer) as HTMLVideoElement;
    player.pause();
    const hasMedia = localStream.getVideoTracks().length > 0;
    if (hasMedia) {
      player.srcObject = localStream;
      await player.play();
    }
  }

  public beforeDestroy() {
    AudioPlayer.stop("beep");
  }
}
